import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { Category, ProjectInterface } from '@rappider/api-sdk';
import { PAGE_DEFINITIONS, PATH_DEFINITIONS, ProcessInfoIdentifier, ProcessInfoStatus } from '@rappider/shared/definitions';
import { orderBy } from 'lodash';
import { PROJECT_LIST_CONFIG_WITH_CARD, PROJECT_LIST_CONFIG, getProjectDeleteConfig, ProjectStructureType } from '@rappider/shared/configs';
import { ABSTRACT_IMAGES } from '@rappider/shared/data';
import { AlertConfig, AlertTypes, ButtonComponentConfig, ButtonType, HeadingComponentConfig, HeadingType, IconType, InputGroupComponentConfig, TextComponentConfig, TextMode, IconComponentConfig } from '@rappider/rappider-components/utils';
import { CreateProject, DeleteProject, FavoritePersonProject, UnFavoritePersonProject } from '../../states/projects-state/project.actions';
import { ClearActiveProject, SelectActiveProject } from '../../states/active-project-state/active-project.actions';
import { Router } from '@angular/router';
import { Person, ProcessInfoWithRelations, Project, ProjectWithRelations } from '@rappider/rappider-sdk';
import { Navigate } from '@rappider/shared';
import { ProjectAction } from './utils/project-action.enum';
import { ProjectFilter } from './utils/project-filter.enum';
import { ProjectFilterOptions } from './utils/project-filter-options';
import { getPersonWithProjects } from './utils/get-person-with-projects.selector';
import { selectLoadingActiveProjectId } from '../../states/projects-state/project.selectors';
import { ProjectCreateService } from '../../services/project-create.service';

@Component({
  selector: 'rappider-projects',
  templateUrl: './project-list.component.html',
  styleUrls: ['./project-list.component.scss']
})
export class ProjectListComponent implements OnInit, OnDestroy {

  isLoading = false;
  isLoaded = false;
  subscriptions: Subscription[];
  person: Person;
  personProjects: (ProjectWithRelations & { isFavorited: boolean })[];
  filteredPersonProjects: (ProjectWithRelations & { isFavorited: boolean })[];
  projectListConfig = PROJECT_LIST_CONFIG;
  projectListWithCard = PROJECT_LIST_CONFIG_WITH_CARD;
  deleteProjectConfig: AlertConfig;
  abstractImages = ABSTRACT_IMAGES;
  displayToolbar = false;
  displayToolbarBackButton = false;
  canCreateProjects = false;
  modalVisibility = false;
  deleteProject: Project;
  confirmationText: string;
  deleteModalOkLoading: boolean;
  projectCreationStatus = false;

  displayedProjects: (ProjectWithRelations & { isFavorited: boolean })[];
  selectedCategoryName: string;

  ProjectFilterOptions = ProjectFilterOptions;

  mainTitle: HeadingComponentConfig = {
    content: PAGE_DEFINITIONS.PROJECTS.CHILDREN.PROJECTS_LIST.PAGE_TITLE,
    type: HeadingType.H2
  };

  addNewProjectButton: ButtonComponentConfig = {
    key: 'create-project',
    text: 'Create New',
    disabled: true,
    icon: {
      name: 'fas fa-lock',
      type: IconType.FontAwesome
    },
    type: ButtonType.Primary
  };
  activeProject: Project;
  loadingActiveProjectId: string;
  lottieConfig = {
    options: {
      autoplay: true,
      loop: true,
      path: 'https://lottie.host/63b3e56a-3b88-4037-be84-2b2fceb88b90/O98MCCVRAX.json'
    },
    height: '200px'
  };
  isStarredProjectsSelected: boolean;
  projectListAlertConfig = {
    type: AlertTypes.Warning,
    title: <HeadingComponentConfig>{
      content: 'You dont have any starred projects',
      type: HeadingType.H6
    },
    closeable: false,
    showIcon: true
  };
  projectListAlertConfigBySearch: any;

  cardListSearchText: string;

  cardSearchInputConfig: InputGroupComponentConfig = {
    textbox: {
      placeholder: 'Search Projects'
    },
    suffixIcon: {
      name: 'fas fa-search',
      type: IconType.FontAwesome
    }
  };

  sharedProjectsAlertConfig = {
    type: AlertTypes.Warning,
    title: <HeadingComponentConfig>{
      content: 'You dont have any shared projects',
      type: HeadingType.H6
    },
    closeable: false,
    showIcon: true
  };

  constructor(
    private store: Store<any>,
    private router: Router,
    private projectCreateService: ProjectCreateService
  ) { }

  ngOnInit() {
    this.selectedCategoryName = localStorage.getItem('selectedCategoryName');
    this.subscribeToData();
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(subscription => subscription.unsubscribe());
  }

  subscribeToData() {
    this.subscriptions = [
      this.subscribeToPersonAndProjects(),
      this.subscribeToLoading(),
      this.subscribeToActiveProject(),
      this.subscribeToGetActiveProjectLoadingId(),
      this.subscribeToChangeFavorite(),
      this.subscribeToDeleteModalOkLoading()
    ];
  }

  subscribeToPersonAndProjects() {
    return this.store.select(<any>getPersonWithProjects).subscribe(getPersonWithProjects => {
      this.person = getPersonWithProjects.person;
      this.personProjects = getPersonWithProjects.projects;
      this.isLoaded = getPersonWithProjects.isLoaded;
      if (getPersonWithProjects?.isLoaded) {
        this.canCreateProjects = this.personProjects?.filter(project =>
          project.createdById === this.person.id)?.length >= this.person?.maxProjectQuota ? false : true;
        this.addNewProjectButton = {
          ...this.addNewProjectButton,
          disabled: !this.canCreateProjects,
          icon: {
            name: (!this.canCreateProjects) ? 'fas fa-lock' : 'fas fa-plus',
            type: IconType.FontAwesome,
            color: (!this.canCreateProjects) ? 'var(--disabled-text-color)' : 'var(--primary-button-text-color)'
          },
          tooltipText: this.getTooltipText(),
          type: ButtonType.Primary
        };

        const sortedProjects = orderBy(this.personProjects, ['createdDate'], 'desc');
        this.filteredPersonProjects = sortedProjects;
        this.displayedProjects = sortedProjects;
        if (this.deleteProject && this.displayedProjects.some(project => project?.id !== this.deleteProject?.id)) {
          this.onCancelModal();
        }
        this.initFilter();
      }
    });
  }

  subscribeToLoading() {
    return this.store.select(state => state.project?.loading).subscribe((loading: boolean) => {
      this.isLoading = loading;
    });
  }

  subscribeToDeleteModalOkLoading() {
    return this.store.select(state => state.project?.deleteModalOkLoading).subscribe((deleteModalOkLoading: boolean) => {
      this.deleteModalOkLoading = deleteModalOkLoading;
    });
  }

  subscribeToGetActiveProjectLoadingId() {
    return this.store.select(selectLoadingActiveProjectId).subscribe((id: string) => {
      this.loadingActiveProjectId = id;
    });
  }

  subscribeToChangeFavorite() {
    return this.store.select(state => state.project?.changeFavorite).subscribe((changeFavorite: boolean) => {
      if (changeFavorite) {
        this.onProjectFilterChange();
      }
    });
  }

  subscribeToActiveProject() {
    return this.store.select(state => state.activeProject.data).subscribe(activeProject => {
      this.activeProject = activeProject;
    });
  }

  getProjectListCardConfig(project: ProjectInterface, index: number) {
    return {
      image: {
        source: project.imagePlaceHolderUrl || this.abstractImages[index % this.abstractImages.length],
        customSizeSettings: {
          width: '100%',
          height: '8rem'
        },
      },
      title: {
        type: HeadingType.H2,
        content: project.name
      }
    };
  }

  /**
   * set active project
   *
   * @param {ActionResponse} actionResponse
   * @memberof ProjectListComponent
   */
  onActiveProjectSelect(project: ProjectInterface) {
    if (project.id) {
      if (this.activeProject?.id !== project.id) {
        this.store.dispatch(new SelectActiveProject({ projectId: project.id, navigateToProjectDetail: true }));
      } else if (this.activeProject) {
        this.router.navigateByUrl(`${PATH_DEFINITIONS.PROJECTS.PROJECT_DETAIL_PATH}/${this.activeProject.id}`);
      }
    }
  }

  showDeleteProjectModal(project: Project) {
    this.modalVisibility = true;
    this.deleteProject = project;
    this.deleteProjectConfig = getProjectDeleteConfig(project.name);
  }

  onDeleteProject() {
    this.store.dispatch(new DeleteProject({ projectId: this.deleteProject.id }));
  }

  onCancelModal() {
    this.deleteProject = null;
    this.modalVisibility = false;
    this.confirmationText = '';
  }

  createProject() {
    this.projectCreateService.createEmptyProject();
  }

  onProjectAction(action, project) {
    switch (action.button.key) {
      case ProjectAction.Delete:
        this.showDeleteProjectModal(project);
        break;
      case ProjectAction.Edit:
        this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PROJECTS.PROJECT_EDIT_PATH}/${project.id}` }));
        break;
      case ProjectAction.Favorite:
        this.store.dispatch(new FavoritePersonProject({ projectId: project.id, isFavorited: true }));
        break;
      case ProjectAction.Unfavorite:
        this.store.dispatch(new UnFavoritePersonProject({ projectId: project.id, isFavorited: false }));
        break;
      default:
        break;
    }
  }

  initFilter() {
    if (!this.selectedCategoryName) {
      this.displayedProjects = this.filteredPersonProjects ?? [];
    } else {
      this.filterProjects();
    }
  }

  onProjectFilterChange() {
    localStorage.setItem('selectedCategoryName', this.selectedCategoryName);
    this.filterProjects();
  }

  private filterProjects() {
    switch (this.selectedCategoryName) {
      case ProjectFilter.All: {
        this.displayedProjects = this.filteredPersonProjects;
        this.isStarredProjectsSelected = false;
        break;
      }
      case ProjectFilter.Starred: {
        this.displayedProjects = this.filteredPersonProjects?.filter((filteredProject) => filteredProject.isFavorited);
        this.isStarredProjectsSelected = true;
        break;
      }
      case ProjectFilter.OwnedByMe: {
        this.displayedProjects = this.filteredPersonProjects?.filter(item => item.createdById === this.person.id);
        this.isStarredProjectsSelected = false;
        break;
      }
      case ProjectFilter.Shared: {
        this.displayedProjects = this.filteredPersonProjects?.filter(item => item.createdById !== this.person.id);
        this.isStarredProjectsSelected = false;
        break;
      }
      default: {
        break;
      }
    }
  }

  getSecondActionButton(project: Project) {
    if (project?.isFavorited) {
      return this.projectListWithCard.secondActionButton = {
        key: ProjectAction.Unfavorite,
        icon: {
          name: 'fa-solid far fa-star',
          type: IconType.FontAwesome,
          color: 'var(--primary-color)'
        },
        popconfirmTitle: '',
        type: ButtonType.Link,
        emitWithoutPopconfirm: true
      };
    } else {
      return this.projectListWithCard.secondActionButton = {
        key: ProjectAction.Favorite,
        icon: {
          name: 'fa-light far fa-star',
          type: IconType.FontAwesome,
          color: null,
        },
        popconfirmTitle: '',
        type: ButtonType.Link,
        emitWithoutPopconfirm: true
      };
    }
  }

  /**
   * Get top right icon configuration based on project structure type
   * @param project - The project object
   * @returns IconComponentConfig for the top right icon
   */
  getTopRightIconConfig(project: ProjectWithRelations & { isFavorited: boolean }): IconComponentConfig {
    const structureType = project.structureType || ProjectStructureType.Application;

    switch (structureType) {
      case ProjectStructureType.Module:
        return {
          name: 'fa-solid fa-layer-group',
          type: IconType.FontAwesome,
          color: 'var(--primary-color)',
          size: '16px'
        };
    }
  }

  getTooltipText(): string {
    return !this.canCreateProjects ? 'Upgrade for Unlimited Projects' : null;
  }

  onActionButtonClick(action: ButtonComponentConfig) {
    if (action.key === 'create-project') {
      this.createProject();
      this.addNewProjectButton = {
        ...this.addNewProjectButton,
        loading: true,
        disabled: true,
        icon: {
          name: (!this.canCreateProjects && this.projectCreationStatus) ? 'fas fa-lock' : 'fas fa-plus',
          type: IconType.FontAwesome,
          color: (!this.canCreateProjects && this.projectCreationStatus) ? 'var(--disabled-text-color)' : 'var(--primary-button-text-color)'
        }
      };
    }
  }

  get filteredProjects() {
    if (
      this.cardListSearchText && this.cardListSearchText.trim() &&
      this.displayedProjects?.length &&
      this.projectListConfig
    ) {
      let filteredData;
      if (this.projectListConfig.multipleSearchFields && this.projectListConfig.multipleSearchFields.length > 0) {
        filteredData = this.displayedProjects.filter(item =>
          this.projectListConfig.multipleSearchFields.some(field => item[field]?.toLowerCase().includes(this.cardListSearchText.trim().toLowerCase()))
        );
      } else if (this.projectListConfig.defaultSearchField) {
        filteredData = this.displayedProjects.filter(item =>
          item[this.projectListConfig.defaultSearchField]?.toLowerCase().includes(this.cardListSearchText.trim().toLowerCase())
        );
      }
      if (!filteredData.length) {
        this.projectListAlertConfigBySearch = {
          type: AlertTypes.Info,
          title: <HeadingComponentConfig>{
            content: `No projects match your search criteria: ${this.cardListSearchText}`,
            type: HeadingType.H6
          },
          closeable: false,
          showIcon: true
        };
      } else {
        this.projectListAlertConfigBySearch = null;
      }
      return filteredData || [];
    }
    return this.displayedProjects || [];
  }

  get deleteProjectWarningDescription(): TextComponentConfig {
    const baseText = 'This will permanently delete the ';
    const projectText = `<b>${this.deleteProject?.name}</b> project`;
    const additionalText = ' with all dependent data and remove all collaborator associations.';

    return {
      content: baseText + projectText + additionalText,
      textMode: TextMode.Html
    };
  }

  get deleteProjectConfirmationText(): TextComponentConfig {
    return {
      content: `To confirm, type  <b>${this.deleteProject?.name}</b> in the box below`,
      textMode: TextMode.Html
    };
  }

}

